"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, CheckCircle2, Info, X } from "lucide-react";
import { useT } from "@/lib/i18n";

export type ToastKind = "success" | "error" | "info";

export interface ToastItem {
  id: string;
  kind: ToastKind;
  message: string;
  ttlMs: number;
}

interface ToastApi {
  success: (message: string, ttlMs?: number) => void;
  error: (message: string, ttlMs?: number) => void;
  info: (message: string, ttlMs?: number) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used inside <ToastProvider>");
  }
  return ctx;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seqRef = useRef(0);

  const push = useCallback((kind: ToastKind, message: string, ttlMs: number) => {
    const id = `t${++seqRef.current}`;
    setItems((prev) => [...prev, { id, kind, message, ttlMs }]);
  }, []);

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (msg, ttl = 2500) => push("success", msg, ttl),
      error: (msg, ttl = 4000) => push("error", msg, ttl),
      info: (msg, ttl = 2500) => push("info", msg, ttl),
      dismiss,
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport items={items} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastViewport({ items, onDismiss }: { items: ToastItem[]; onDismiss: (id: string) => void }) {
  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-2 pointer-events-none"
    >
      {items.map((t) => (
        <ToastCard key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

const KIND_STYLES: Record<ToastKind, { bg: string; border: string; color: string; Icon: typeof Info }> = {
  success: { bg: "var(--success-bg, #dcfce7)", border: "var(--success, #16a34a)", color: "var(--success, #15803d)", Icon: CheckCircle2 },
  error: { bg: "var(--error-bg, #fee2e2)", border: "var(--error, #dc2626)", color: "var(--error, #b91c1c)", Icon: AlertCircle },
  info: { bg: "var(--info-bg, #dbeafe)", border: "var(--info, #2563eb)", color: "var(--info, #1d4ed8)", Icon: Info },
};

function ToastCard({ toast, onDismiss }: { toast: ToastItem; onDismiss: (id: string) => void }) {
  const t = useT();
  const { kind, message, ttlMs, id } = toast;
  const { bg, border, color, Icon } = KIND_STYLES[kind];

  useEffect(() => {
    if (ttlMs <= 0) return;
    const timer = window.setTimeout(() => onDismiss(id), ttlMs);
    return () => window.clearTimeout(timer);
  }, [id, ttlMs, onDismiss]);

  return (
    <div
      role={kind === "error" ? "alert" : "status"}
      className="pointer-events-auto flex items-start gap-2 min-w-[260px] max-w-[420px] px-3 py-2 rounded-lg shadow-lg border text-base"
      style={{ backgroundColor: bg, borderColor: border, color }}
    >
      <Icon className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="flex-1 leading-snug whitespace-pre-line">{message}</div>
      <button
        onClick={() => onDismiss(id)}
        aria-label={t("common.dismissNotification")}
        title={t("common.dismiss")}
        className="p-0.5 rounded hover:bg-black/5 cursor-pointer"
      >
        <X className="w-3.5 h-3.5" style={{ color }} />
      </button>
    </div>
  );
}
